import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Check, X, Compass, UserCheck, Image as ImageIcon, Sparkles } from "lucide-react";
import { Button } from "./ui/button";

const STEP_ORDER = [
  {
    key: "add_to_collection",
    title: "Add your first release",
    body: "Browse the catalog and tap any card to add it to your collection.",
    cta: { label: "Browse catalog", href: "/catalog" },
    Icon: Compass,
    accent: "rose",
  },
  {
    key: "made_public",
    title: "Make your profile public",
    body: "Show off your collection, get followers, and appear on leaderboards.",
    cta: { label: "Profile settings", href: "/profile" },
    Icon: UserCheck,
    accent: "purple",
  },
  {
    key: "customized_profile",
    title: "Personalize your profile",
    body: "Add a photo, a short bio, and pick a custom username.",
    cta: { label: "Edit profile", href: "/profile" },
    Icon: ImageIcon,
    accent: "amber",
  },
];

const ACCENTS = {
  rose: { ring: "border-rose-500/30", icon: "text-rose-400 bg-rose-500/10", btn: "bg-rose-600 hover:bg-rose-500" },
  purple: { ring: "border-purple-500/30", icon: "text-purple-400 bg-purple-500/10", btn: "bg-purple-600 hover:bg-purple-500" },
  amber: { ring: "border-amber-500/30", icon: "text-amber-400 bg-amber-500/10", btn: "bg-amber-600 hover:bg-amber-500" },
};

export default function OnboardingPanel() {
  const [status, setStatus] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/onboarding/status")
      .then(({ data }) => { if (!cancelled) setStatus(data); })
      .catch(() => { if (!cancelled) setStatus({ should_show: false }); });
    return () => { cancelled = true; };
  }, []);

  if (!status || !status.should_show || hidden) return null;
  const completedCount = STEP_ORDER.filter((s) => status.steps?.[s.key]).length;
  const total = STEP_ORDER.length;
  const pct = Math.round((completedCount / total) * 100);

  const handleDismiss = async () => {
    setHidden(true);
    try { await api.post("/onboarding/dismiss"); } catch { /* no-op */ }
  };

  return (
    <div
      data-testid="onboarding-panel"
      className="rounded-2xl bg-gradient-to-br from-zinc-900/80 via-rose-950/10 to-zinc-900/80 border border-rose-500/20 p-5 sm:p-6 relative overflow-hidden"
    >
      <button
        data-testid="onboarding-dismiss"
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1.5 rounded-full text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800/80 transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-rose-500/10 text-rose-400">
          <Sparkles className="w-5 h-5" />
        </div>
        <div>
          <h2 className="font-heading text-lg font-bold text-zinc-100">Welcome aboard</h2>
          <p className="text-xs text-zinc-500">Finish setup to unlock the full experience.</p>
        </div>
        <div className="ml-auto text-right">
          <p className="font-mono text-sm text-rose-400">{completedCount}/{total}</p>
          <p className="text-[10px] uppercase tracking-wider text-zinc-600">{pct}% done</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        {STEP_ORDER.map((step) => {
          const done = !!status.steps?.[step.key];
          const accent = ACCENTS[step.accent];
          const Icon = step.Icon;
          return (
            <div
              key={step.key}
              data-testid={`onboarding-step-${step.key}`}
              className={`rounded-xl bg-zinc-950/40 border ${done ? "border-emerald-500/25" : accent.ring} p-4 flex flex-col gap-3`}
            >
              <div className="flex items-start justify-between">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${done ? "bg-emerald-500/15 text-emerald-400" : accent.icon}`}>
                  {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                {done && (
                  <span className="text-[9px] font-mono uppercase tracking-wider text-emerald-400">Done</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${done ? "text-zinc-300 line-through decoration-emerald-500/40" : "text-zinc-100"}`}>{step.title}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">{step.body}</p>
              </div>
              {!done && (
                <Link to={step.cta.href}>
                  <Button data-testid={`onboarding-cta-${step.key}`} size="sm" className={`w-full ${accent.btn} text-white text-xs`}>
                    {step.cta.label}
                  </Button>
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
